/**
 * MessageInput component - text input with attachments, voice notes, emoji, mentions
 * WhatsApp-style bottom bar for message composition
 */
'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { 
  Send, Paperclip, Camera, FolderOpen, Mic, MicOff, Trash2,
  X, Smile, AtSign, Reply, MapPin, File as FileIcon, Lock, ChevronUp
} from 'lucide-react';
import type { Attachment, ThreadMessage, ThreadMember } from './types';
import haptics from '@/lib/haptics';
import { notify } from '@/lib/notifications';
import { isCapacitor } from '@/lib/platformUtils';
import {
  createSpeechToTextController,
  isSpeechToTextSupported,
  type SpeechToTextController,
} from '@/lib/speechToText';
import useCamera, { SimpleCameraModal } from '@/hooks/useCamera';
import { uiViewportHeight, uiViewportWidth } from '@/lib/uiScale';
import { dataUrlToFile } from '@/lib/compressImage';

// Enter key behavior preference
export type EnterKeyBehavior = 'send' | 'newline';

interface EmojiPickerPosition {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
}

// Load enter key preference from localStorage with platform-appropriate default
export function getEnterKeyPreference(): EnterKeyBehavior {
  if (typeof window === 'undefined') return 'send';
  const saved = localStorage.getItem('ta:enterKeyBehavior');
  if (saved === 'send' || saved === 'newline') return saved;
  // Default: mobile = newline, desktop/web = send
  return isCapacitor() ? 'newline' : 'send';
}

// Save enter key preference
export function setEnterKeyPreference(behavior: EnterKeyBehavior): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem('ta:enterKeyBehavior', behavior);
  // Dispatch event so other components can react
  window.dispatchEvent(new CustomEvent('ta:enterKeyBehaviorChanged', { detail: behavior }));
}

interface MessageInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  onAttachmentUpload: (file: File) => Promise<Attachment | null>;
  onComposerFocus?: () => void;
  
  // State
  isDark?: boolean;
  disabled?: boolean;
  sending?: boolean;
  placeholder?: string;
  
  // Attachments
  pendingAttachments?: Attachment[];
  onRemoveAttachment?: (attachmentId: string) => void;
  uploadingAttachment?: boolean;
  
  // Reply
  replyTo?: ThreadMessage | null;
  onCancelReply?: () => void;
  
  // Voice notes
  onVoiceNote?: (audioBlob: Blob, duration: number) => void;
  
  // Location sharing
  onShareLocation?: () => void;
  sharingLocation?: boolean;
  
  // Mentions
  members?: ThreadMember[];
  onMention?: (userId: number) => void;
  
  // Typing indicator
  onTyping?: () => void;
  onStoppedTyping?: () => void;
  
  // Speech to text
  enableSpeechToText?: boolean;
}

// Helper to format duration for voice notes
function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}


// Common emoji categories
const EMOJI_CATEGORIES = {
  recent: [],
  smileys: ['😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂', '😉', '😌', '😍', '🥰', '😘', '😎', '🤩', '😴', '🤯', '🥳'],
  gestures: ['👍', '👎', '👌', '✌️', '🤞', '🤟', '🤙', '👋', '🙏', '👏', '🤝', '💪', '🙌', '🫡', '✍️'],
  hearts: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💔', '❣️', '💕', '💗', '💓', '💞'],
  work: ['✅', '❌', '⭐', '🔥', '💯', '⚠️', '🚀', '💡', '🎉', '🎊', '📌', '🛠️', '📈', '🧠', '⏰'],
  nature: ['☀️', '🌧️', '🌈', '🌍', '🌟', '🌸', '🌴', '🌊', '🍀', '🍎'],
  travel: ['🚗', '🚚', '✈️', '🚀', '🏁', '📍', '🗺️', '🏢', '🏠', '🧭']
};

export default function MessageInput({
  value,
  onChange,
  onSend,
  onAttachmentUpload,
  onComposerFocus,
  isDark = false,
  disabled = false,
  sending = false,
  placeholder = 'Type a message...',
  pendingAttachments = [],
  onRemoveAttachment,
  uploadingAttachment = false,
  replyTo,
  onCancelReply,
  onVoiceNote,
  onShareLocation,
  sharingLocation = false,
  members = [],
  onMention,
  onTyping,
  onStoppedTyping,
  enableSpeechToText = false
}: MessageInputProps) {
  // Refs
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const attachMenuRef = useRef<HTMLDivElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const emojiPickerPanelRef = useRef<HTMLDivElement>(null);
  const mentionDropdownRef = useRef<HTMLDivElement>(null);
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingStartedAtRef = useRef<number | null>(null);
  const isRecordingRef = useRef(false);
  const recordingStopModeRef = useRef<'preview' | 'send' | 'cancel'>('preview');
  const pendingRecordingStopModeRef = useRef<'send' | 'cancel' | null>(null);
  const activeRecordingGestureRef = useRef(false);
  const recordingPointerIdRef = useRef<number | null>(null);
  const recordingStartPointRef = useRef<{ x: number; y: number } | null>(null);
  const [isRecordingLocked, setIsRecordingLocked] = useState(false);
  const [recordingLockProgress, setRecordingLockProgress] = useState(0);
  
  // Speech-to-text state
  const [isDictating, setIsDictating] = useState(false);
  const [dictationSupported, setDictationSupported] = useState(false);
  const dictationControllerRef = useRef<SpeechToTextController | null>(null);
  const dictationBaseValueRef = useRef('');
  
  // UI state
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [emojiPickerPosition, setEmojiPickerPosition] = useState<EmojiPickerPosition | null>(null);
  const [mentionFilter, setMentionFilter] = useState('');
  const [mentionCursorPosition, setMentionCursorPosition] = useState(0);
  
  // Typing indicator debounce
  const lastTypingSentRef = useRef<number>(0);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Detect if running in Capacitor for safe-area handling
  const isCapacitorApp = isCapacitor();

  const handleCapturedCameraPhoto = useCallback(async (base64: string) => {
    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const file = await dataUrlToFile(base64, `camera-capture-${timestamp}.jpg`);
      await onAttachmentUpload(file);
    } catch (error) {
      console.error('Failed to process camera capture:', error);
      notify.error('Unable to attach the captured photo right now', 3500, 'message-camera-capture-upload');
    }
  }, [onAttachmentUpload]);

  const camera = useCamera({
    initialFacingMode: 'environment',
    quality: 0.9,
    onCapture: (base64) => {
      void handleCapturedCameraPhoto(base64);
    },
  });

  const updateEmojiPickerPosition = useCallback(() => {
    const trigger = emojiButtonRef.current;
    const panel = emojiPickerPanelRef.current;

    if (!trigger || typeof window === 'undefined') {
      return;
    }

    const viewportPadding = 8;
    const triggerRect = trigger.getBoundingClientRect();
    const viewportWidth = uiViewportWidth();
    const viewportHeight = uiViewportHeight();
    const pickerWidth = Math.min(280, viewportWidth - viewportPadding * 2);
    const measuredHeight = panel?.offsetHeight ?? 260;
    const availableAbove = triggerRect.top - viewportPadding;
    const availableBelow = viewportHeight - triggerRect.bottom - viewportPadding;
    const prefersAbove = availableAbove >= 180 || availableAbove >= availableBelow;
    const maxHeight = Math.max(140, Math.min(320, (prefersAbove ? availableAbove : availableBelow) - 8));

    let left = triggerRect.left;
    if (left + pickerWidth > viewportWidth - viewportPadding) {
      left = viewportWidth - pickerWidth - viewportPadding;
    }
    left = Math.max(viewportPadding, left);

    let top = prefersAbove
      ? triggerRect.top - Math.min(measuredHeight, maxHeight) - 8
      : triggerRect.bottom + 8;

    if (top + measuredHeight > viewportHeight - viewportPadding) {
      top = viewportHeight - Math.min(measuredHeight, maxHeight) - viewportPadding;
    }
    top = Math.max(viewportPadding, top);

    setEmojiPickerPosition({
      top,
      left,
      width: pickerWidth,
      maxHeight,
    });
  }, []);

  // Detect speech-to-text support
  useEffect(() => {
    let cancelled = false;

    if (!enableSpeechToText || Boolean(onVoiceNote)) {
      setDictationSupported(false);
      return () => {
        cancelled = true;
      };
    }

    void isSpeechToTextSupported().then((supported) => {
      if (!cancelled) {
        setDictationSupported(supported);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [enableSpeechToText, onVoiceNote]);

  const appendDictationTranscript = useCallback((baseValue: string, transcript: string) => {
    const trimmedTranscript = transcript.trim();
    if (!trimmedTranscript) {
      return baseValue;
    }

    const spacer = baseValue && !baseValue.endsWith(' ') && !baseValue.endsWith('\n') ? ' ' : '';
    return `${baseValue}${spacer}${trimmedTranscript}`;
  }, []);

  // Stop dictation handler
  const stopDictation = useCallback(async () => {
    const activeController = dictationControllerRef.current;
    dictationControllerRef.current = null;

    try {
      if (activeController) {
        await activeController.stop();
        await activeController.destroy();
      }
    } catch {
      // ignore
    } finally {
      setIsDictating(false);
    }
  }, []);

  // Start dictation handler
  const startDictation = useCallback(async () => {
    const supported = dictationSupported || await isSpeechToTextSupported();
    setDictationSupported(supported);

    if (!supported) {
      return;
    }

    if (dictationControllerRef.current) {
      await dictationControllerRef.current.destroy();
      dictationControllerRef.current = null;
    }

    dictationBaseValueRef.current = value;

    const controller = createSpeechToTextController({
      continuous: false,
      interimResults: true,
      autoRestart: false,
      maxAlternatives: 1,
      androidPopup: true,
      androidPrompt: 'Speak your message',
      onListeningChange: setIsDictating,
      onResult: (snapshot) => {
        onChange(appendDictationTranscript(dictationBaseValueRef.current, snapshot.transcript));
      },
      onError: (error) => {
        if (error.code === 'aborted' || error.code === 'no-speech' || error.code === 'no-match') {
          return;
        }

        haptics.error();
        notify.error(error.message, 4000, `message-dictation-${error.code}`);
      },
    });

    dictationControllerRef.current = controller;
    const started = await controller.start();

    if (!started) {
      await controller.destroy();
      if (dictationControllerRef.current === controller) {
        dictationControllerRef.current = null;
      }
      setIsDictating(false);
      notify.error('Unable to start speech-to-text right now', 4000, 'message-dictation-start-failed');
    }
  }, [appendDictationTranscript, dictationSupported, onChange, value]);

  // Toggle dictation
  const toggleDictation = useCallback(() => {
    if (isDictating) {
      void stopDictation();
    } else {
      void startDictation();
    }
  }, [isDictating, stopDictation, startDictation]);

  // Close menus on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (attachMenuRef.current && !attachMenuRef.current.contains(event.target as Node)) {
        setShowAttachMenu(false);
      }
      const emojiTarget = event.target as Node;
      const clickedEmojiTrigger = !!emojiPickerRef.current?.contains(emojiTarget);
      const clickedEmojiPanel = !!emojiPickerPanelRef.current?.contains(emojiTarget);
      if (!clickedEmojiTrigger && !clickedEmojiPanel) {
        setShowEmojiPicker(false);
      }
      if (mentionDropdownRef.current && !mentionDropdownRef.current.contains(event.target as Node)) {
        setShowMentionDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!showEmojiPicker) {
      setEmojiPickerPosition(null);
      return;
    }

    const syncPosition = () => updateEmojiPickerPosition();
    const frameId = window.requestAnimationFrame(syncPosition);

    window.addEventListener('resize', syncPosition);
    window.addEventListener('scroll', syncPosition, true);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', syncPosition);
      window.removeEventListener('scroll', syncPosition, true);
    };
  }, [showEmojiPicker, updateEmojiPickerPosition]);

  // Auto-resize textarea
  const adjustTextareaHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const maxHeight = 240;
      textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    }
  }, []);

  useEffect(() => {
    adjustTextareaHeight();
  }, [value, adjustTextareaHeight]);

  // Handle input change with typing indicator and mention detection
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    
    // Send typing indicator (throttled to once per 2 seconds)
    const now = Date.now();
    if (now - lastTypingSentRef.current > 2000) {
      onTyping?.();
      lastTypingSentRef.current = now;
    }
    
    // Clear stopped typing timeout and set new one
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      onStoppedTyping?.();
    }, 3000);
    
    // Detect @ mentions
    const cursorPos = e.target.selectionStart || 0;
    const textBeforeCursor = newValue.substring(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@(\w*)$/);
    
    if (atMatch) {
      setShowMentionDropdown(true);
      setMentionFilter(atMatch[1].toLowerCase());
      setMentionCursorPosition(cursorPos - atMatch[1].length - 1);
    } else {
      setShowMentionDropdown(false);
    }
  };

  // Handle paste - support images from clipboard
  const handlePaste = useCallback(async (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    
    // Check for image files in clipboard
    const imageItems: DataTransferItem[] = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        imageItems.push(item);
      }
    }
    
    // If we have images, handle them
    if (imageItems.length > 0) {
      e.preventDefault(); // Prevent default paste of image as text
      
      for (const item of imageItems) {
        const file = item.getAsFile();
        if (file) {
          // Generate a better filename based on timestamp
          const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
          const extension = file.type.split('/')[1] || 'png';
          const namedFile = new File([file], `pasted-image-${timestamp}.${extension}`, { type: file.type });
          
          console.log('[MessageInput] 📋 Pasting image from clipboard:', namedFile.name, namedFile.type);
          await onAttachmentUpload(namedFile);
        }
      }
      return;
    }
    
    // For text, let default paste behavior handle it
  }, [onAttachmentUpload]);

  // Handle send
  const handleSend = () => {
    if (disabled || sending) return;
    if (!value.trim() && pendingAttachments.length === 0) return;

    if (isDictating) {
      void stopDictation();
    }
    
    // Haptic feedback handled in parent component
    onStoppedTyping?.();
    onSend();
    
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  // Handle key events
  const handleKeyDown = (e: React.KeyboardEvent) => {
    const enterBehavior = getEnterKeyPreference();
    
    // Ctrl+Enter or Cmd+Enter always does the opposite of Enter
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (enterBehavior === 'send') {
        // Insert newline when Ctrl+Enter and Enter sends
        const textarea = textareaRef.current;
        if (textarea) {
          const start = textarea.selectionStart;
          const end = textarea.selectionEnd;
          const newValue = value.substring(0, start) + '\n' + value.substring(end);
          onChange(newValue);
          // Move cursor after newline
          setTimeout(() => {
            textarea.selectionStart = textarea.selectionEnd = start + 1;
          }, 0);
        }
      } else {
        // Send when Ctrl+Enter and Enter adds newline
        handleSend();
      }
      return;
    }
    
    // Shift+Enter always adds newline
    if (e.key === 'Enter' && e.shiftKey) {
      // Let default behavior add newline
      return;
    }
    
    // Plain Enter - behavior based on user preference
    if (e.key === 'Enter') {
      if (enterBehavior === 'send') {
        e.preventDefault();
        handleSend();
      }
      // If 'newline', let default behavior add newline
      return;
    }
    
    // Close mention dropdown on Escape
    if (e.key === 'Escape') {
      setShowMentionDropdown(false);
      setShowEmojiPicker(false);
      setShowAttachMenu(false);
    }
  };

  // Insert mention
  const insertMention = (member: ThreadMember) => {
    const beforeMention = value.substring(0, mentionCursorPosition);
    const afterMention = value.substring(textareaRef.current?.selectionStart || mentionCursorPosition);
    const newValue = `${beforeMention}@${member.display_name} ${afterMention}`;
    onChange(newValue);
    setShowMentionDropdown(false);
    onMention?.(member.user_id);
    textareaRef.current?.focus();
  };

  // Insert emoji
  const insertEmoji = (emoji: string) => {
    const cursorPos = textareaRef.current?.selectionStart || value.length;
    const newValue = value.substring(0, cursorPos) + emoji + value.substring(cursorPos);
    onChange(newValue);
    textareaRef.current?.focus();
  };

  // Handle file selection
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    for (const file of Array.from(files)) {
      await onAttachmentUpload(file);
    }
    
    // Reset input
    e.target.value = '';
    setShowAttachMenu(false);
  };

  const openCameraCapture = useCallback(() => {
    setShowAttachMenu(false);
    camera.open();
  }, [camera]);

  // Voice recording
  const startRecording = async () => {
    try {
      if (isRecordingRef.current || isDictating) {
        return;
      }

      if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
        notify.error('Voice recording is not supported on this device', 3500, 'message-voice-unsupported');
        return;
      }

      haptics.medium(); // Haptic feedback for starting recording
      pendingRecordingStopModeRef.current = null;
      recordingStopModeRef.current = 'preview';
      setIsRecordingLocked(false);
      setRecordingLockProgress(0);

      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      setAudioBlob(null);
      setAudioUrl(null);

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const preferredMimeType = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
      ].find((mimeType) => MediaRecorder.isTypeSupported?.(mimeType));
      const mediaRecorder = preferredMimeType
        ? new MediaRecorder(stream, { mimeType: preferredMimeType })
        : new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const stoppedAt = Date.now();
        const startedAt = recordingStartedAtRef.current || stoppedAt;
        const duration = Math.max(1, Math.round((stoppedAt - startedAt) / 1000));
        const blob = new Blob(audioChunksRef.current, { type: preferredMimeType || 'audio/webm' });
        const stopMode = recordingStopModeRef.current;

        if (stopMode === 'send' && onVoiceNote) {
          haptics.success();
          onVoiceNote(blob, duration);
          setAudioBlob(null);
          if (audioUrl) {
            URL.revokeObjectURL(audioUrl);
            setAudioUrl(null);
          }
        } else if (stopMode !== 'cancel') {
          setAudioBlob(blob);
          setAudioUrl(URL.createObjectURL(blob));
        } else {
          if (audioUrl) {
            URL.revokeObjectURL(audioUrl);
            setAudioUrl(null);
          }
          setAudioBlob(null);
        }

        stream.getTracks().forEach(track => track.stop());
        mediaRecorderRef.current = null;
        isRecordingRef.current = false;
        recordingStartedAtRef.current = null;
        setIsRecording(false);
        setIsRecordingLocked(false);
        setRecordingLockProgress(0);
        setRecordingDuration(0);

        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }
      };
      
      mediaRecorder.start();
      isRecordingRef.current = true;
      setIsRecording(true);
      setRecordingDuration(0);
      recordingStartedAtRef.current = Date.now();
      
      // Start duration timer
      recordingTimerRef.current = setInterval(() => {
        const startedAt = recordingStartedAtRef.current;
        if (!startedAt) return;
        setRecordingDuration(Math.max(1, Math.round((Date.now() - startedAt) / 1000)));
      }, 250);

      if (pendingRecordingStopModeRef.current) {
        const stopMode = pendingRecordingStopModeRef.current;
        pendingRecordingStopModeRef.current = null;
        window.setTimeout(() => {
          void stopRecording(stopMode);
        }, 0);
      }
    } catch (error) {
      console.error('Failed to start recording:', error);
      haptics.error();
      notify.error('Unable to start voice recording', 3500, 'message-voice-start');
      isRecordingRef.current = false;
      recordingStartedAtRef.current = null;
      setIsRecording(false);
      setRecordingDuration(0);
    }
  };

  async function stopRecording(mode: 'preview' | 'send' | 'cancel' = 'preview') {
    recordingStopModeRef.current = mode;

    if (!mediaRecorderRef.current || !isRecordingRef.current) {
      pendingRecordingStopModeRef.current = mode === 'preview' ? null : mode;
      return;
    }

    haptics.light(); // Haptic feedback for stopping

    if (mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  }

  const cancelRecording = useCallback(() => {
    haptics.warning(); // Haptic feedback for cancel
    recordingStopModeRef.current = 'cancel';
    pendingRecordingStopModeRef.current = 'cancel';
    if (mediaRecorderRef.current && isRecordingRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream?.getTracks().forEach(track => track.stop());
    }
    isRecordingRef.current = false;
    recordingStartedAtRef.current = null;
    setIsRecording(false);
    setIsRecordingLocked(false);
    setRecordingLockProgress(0);
    setAudioBlob(null);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
    setRecordingDuration(0);
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
  }, [audioUrl]);

  const resetRecordingGesture = useCallback(() => {
    activeRecordingGestureRef.current = false;
    recordingPointerIdRef.current = null;
    recordingStartPointRef.current = null;
  }, []);

  useEffect(() => {
    const handlePointerUp = (event: PointerEvent) => {
      if (!activeRecordingGestureRef.current) {
        return;
      }

      if (recordingPointerIdRef.current !== null && event.pointerId !== recordingPointerIdRef.current) {
        return;
      }

      resetRecordingGesture();
      void stopRecording('send');
    };

    const handlePointerCancel = (event: PointerEvent) => {
      if (!activeRecordingGestureRef.current) {
        return;
      }

      if (recordingPointerIdRef.current !== null && event.pointerId !== recordingPointerIdRef.current) {
        return;
      }

      resetRecordingGesture();
      cancelRecording();
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (!activeRecordingGestureRef.current || !recordingStartPointRef.current) {
        return;
      }

      if (recordingPointerIdRef.current !== null && event.pointerId !== recordingPointerIdRef.current) {
        return;
      }

      const dx = event.clientX - recordingStartPointRef.current.x;
      const dy = event.clientY - recordingStartPointRef.current.y;

      // WhatsApp-style gestures while holding the mic button:
      // slide up ≥70px → lock (recording continues hands-free)
      // slide left ≥70px → cancel
      setRecordingLockProgress(dy < 0 ? Math.min(1, -dy / 70) : 0);

      if (dx <= -70 && Math.abs(dx) >= Math.abs(dy)) {
        resetRecordingGesture();
        setRecordingLockProgress(0);
        cancelRecording();
        return;
      }

      if (dy <= -70 && Math.abs(dy) > Math.abs(dx)) {
        resetRecordingGesture();
        setRecordingLockProgress(0);
        setIsRecordingLocked(true);
        haptics.light();
      }
    };

    const handleBlur = () => {
      if (!activeRecordingGestureRef.current) {
        return;
      }

      resetRecordingGesture();
      cancelRecording();
    };

    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerCancel);
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerCancel);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('blur', handleBlur);
    };
  }, [cancelRecording, resetRecordingGesture]);

  const sendVoiceNote = () => {
    if (audioBlob && onVoiceNote) {
      haptics.success(); // Haptic feedback for sending voice note
      onVoiceNote(audioBlob, recordingDuration);
      setAudioBlob(null);
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
        setAudioUrl(null);
      }
      setRecordingDuration(0);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (dictationControllerRef.current) {
        void dictationControllerRef.current.destroy();
        dictationControllerRef.current = null;
      }
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      resetRecordingGesture();
    };
  }, [audioUrl, resetRecordingGesture]);

  // Filter members for mention dropdown
  const filteredMembers = members.filter(m => 
    m.display_name.toLowerCase().includes(mentionFilter)
  ).slice(0, 5);

  const composerSurfaceColor = isDark ? '#111827' : '#ffffff';
  const composerTopSpacerHeight = isCapacitorApp ? 10 : 8;
  const composerBottomPadding = isCapacitorApp
    ? '8px'
    : 'max(env(safe-area-inset-bottom, 0px), 8px)';
  const emojiPickerPortal = showEmojiPicker && emojiPickerPosition && typeof document !== 'undefined'
    ? createPortal(
        <div
          ref={emojiPickerPanelRef}
          className={`fixed p-2 rounded-lg shadow-2xl border z-[180] ${
            isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}
          style={{
            top: `${emojiPickerPosition.top}px`,
            left: `${emojiPickerPosition.left}px`,
            width: `${emojiPickerPosition.width}px`,
            maxHeight: `${emojiPickerPosition.maxHeight}px`,
          }}
        >
          <div className="overflow-y-auto pr-1" style={{ maxHeight: `${emojiPickerPosition.maxHeight}px` }}>
            {Object.entries(EMOJI_CATEGORIES).map(([category, emojis]) => (
              emojis.length > 0 && (
                <div key={category} className="mb-2 last:mb-0">
                  <p className={`text-xs font-medium mb-1 capitalize ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {category}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {emojis.map(emoji => (
                      <button
                        key={emoji}
                        onClick={() => {
                          haptics.light();
                          insertEmoji(emoji);
                          setShowEmojiPicker(false);
                        }}
                        className="text-xl hover:scale-125 transition-transform p-1"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <div 
        className={`relative z-30 px-2 border-t flex-shrink-0 overflow-visible transition-[background-color] duration-200 ${isDark ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-white'}`}
        style={{
          paddingTop: 0,
          paddingBottom: composerBottomPadding,
          backgroundColor: composerSurfaceColor,
        }}
      >
        <div
          aria-hidden="true"
          className="w-full flex-shrink-0 transition-[height,background-color] duration-200"
          style={{
            height: `${composerTopSpacerHeight}px`,
            backgroundColor: composerSurfaceColor,
          }}
        />

      {/* Reply preview bar */}
      {replyTo && (
        <div className={`flex items-center gap-2 mb-2 p-2 rounded-lg border-l-4 border-blue-500 ${
          isDark ? 'bg-gray-800' : 'bg-gray-200'
        }`}>
          <Reply className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
          <div className="flex-1 min-w-0">
            <p className={`text-xs font-medium ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
              Replying to {replyTo.sender_name}
            </p>
            <p className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {replyTo.content || (replyTo.attachments?.length ? '📎 Attachment' : '')}
            </p>
          </div>
          <button
            onClick={onCancelReply}
            className={`p-1 rounded ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-300'}`}
          >
            <X className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
          </button>
        </div>
      )}
      
      {/* Pending attachments preview */}
      {pendingAttachments.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2 p-2 rounded-lg" style={{ backgroundColor: isDark ? 'rgba(55, 65, 81, 0.5)' : 'rgba(229, 231, 235, 0.5)' }}>
          {pendingAttachments.map(att => (
            <div key={att.id} className="relative group">
              {att.isImage ? (
                <img 
                  src={att.url} 
                  alt={att.originalFilename || att.filename || 'attachment'}
                  className="w-16 h-16 object-cover rounded-lg"
                />
              ) : (
                <div className={`w-16 h-16 rounded-lg flex flex-col items-center justify-center ${
                  isDark ? 'bg-gray-700' : 'bg-gray-200'
                }`}>
                  <FileIcon className={`w-6 h-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                  <span className={`text-xs mt-1 truncate w-14 text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {(att.originalFilename || att.filename || 'file').split('.').pop()?.toUpperCase() || 'FILE'}
                  </span>
                </div>
              )}
              <button
                onClick={() => onRemoveAttachment?.(att.id)}
                className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      
      {/* Input area */}
      <div className="relative z-30 flex items-end gap-1.5 overflow-visible">
        {/* Hidden file inputs */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,video/*,.pdf,.doc,.docx,.txt,.xls,.xlsx,.zip,.rar"
          onChange={handleFileSelect}
          className="hidden"
        />
        
        {/* Emoji + Attachment stacked vertically */}
        <div className="flex flex-col items-center gap-0.5">
          {/* Emoji picker button — above attach */}
          <div className="relative" ref={emojiPickerRef}>
            <button
              ref={emojiButtonRef}
              onClick={() => {
                haptics.selection();
                setShowEmojiPicker(!showEmojiPicker);
              }}
              className={`p-2 rounded-full transition-colors ${
                isDark ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
              } ${showEmojiPicker ? (isDark ? 'bg-gray-700' : 'bg-gray-200') : ''}`}
              title="Emoji"
            >
              <Smile className="w-5 h-5" />
            </button>
          </div>

          {/* Attachment button with menu */}
          <div className="relative" ref={attachMenuRef}>
            <button
              onClick={() => setShowAttachMenu(!showAttachMenu)}
              disabled={uploadingAttachment || isRecording}
              className={`p-2 rounded-full transition-colors ${
                isDark ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
              } ${uploadingAttachment || isRecording ? 'opacity-50 cursor-not-allowed' : ''} ${showAttachMenu ? (isDark ? 'bg-gray-700' : 'bg-gray-200') : ''}`}
              title="Attach file"
            >
              {uploadingAttachment ? (
                <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <Paperclip className="w-5 h-5" />
              )}
            </button>
          
          {/* Attachment menu */}
          {showAttachMenu && (
            <div className={`absolute bottom-full left-0 mb-2 py-1 rounded-lg shadow-2xl border min-w-[160px] z-[140] ${
              isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            }`}>
              <button
                onClick={() => {
                  fileInputRef.current?.click();
                  setShowAttachMenu(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                  isDark ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <FolderOpen className="w-5 h-5 text-blue-500" />
                <span className="text-sm font-medium">Browse Files</span>
              </button>
              <button
                onClick={() => {
                  void openCameraCapture();
                }}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                  isDark ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                <Camera className="w-5 h-5 text-green-500" />
                <span className="text-sm font-medium">Take Photo</span>
              </button>
              {onShareLocation && (
                <button
                  onClick={() => {
                    onShareLocation();
                    setShowAttachMenu(false);
                  }}
                  disabled={sharingLocation}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                    isDark ? 'hover:bg-gray-700 text-gray-200' : 'hover:bg-gray-100 text-gray-700'
                  } ${sharingLocation ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {sharingLocation ? (
                    <div className="w-5 h-5 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <MapPin className="w-5 h-5 text-red-500" />
                  )}
                  <span className="text-sm font-medium">
                    {sharingLocation ? 'Getting location...' : 'Share Location'}
                  </span>
                </button>
              )}
            </div>
          )}
        </div>
        </div>
        
        {/* Recording UI or text input */}
        {isRecording ? (
          isRecordingLocked ? (
            // Locked recording — hands-free; send or discard without holding
            <div className="flex-1 flex items-center gap-3 px-4 py-2">
              <button
                onClick={cancelRecording}
                className={`p-2 rounded-full transition-colors ${
                  isDark ? 'text-gray-400 hover:text-red-400 hover:bg-gray-700' : 'text-gray-500 hover:text-red-500 hover:bg-gray-200'
                }`}
                title="Discard voice note"
              >
                <Trash2 className="w-5 h-5" />
              </button>
              <div className="flex-1 flex items-center justify-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                <span className={`text-sm font-mono ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  {formatDuration(recordingDuration)}
                </span>
              </div>
              <button
                onClick={() => void stopRecording('send')}
                disabled={sending}
                className={`p-2.5 rounded-full transition-all ${
                  sending ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600'
                } text-white`}
                title="Send voice note"
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          ) : (
            // Holding to record — release sends, slide left cancels, slide up locks
            <div className="flex-1 flex items-center gap-3 px-4 py-2">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                <span className={`text-sm font-mono ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  {formatDuration(recordingDuration)}
                </span>
              </div>
              <span className={`flex-1 text-center text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                ◀ Slide to cancel · Release to send
              </span>
              <div
                className={`flex flex-col items-center transition-colors duration-150 ${
                  recordingLockProgress >= 0.7
                    ? isDark ? 'text-green-400' : 'text-green-600'
                    : isDark ? 'text-gray-400' : 'text-gray-500'
                }`}
                style={{ transform: `translateY(${recordingLockProgress * -4}px)` }}
                title="Slide up to lock recording"
              >
                <ChevronUp className="w-4 h-4 animate-bounce" />
                <Lock className="w-4 h-4" />
              </div>
            </div>
          )
        ) : audioBlob ? (
          // Voice note preview (after recording)
          <div className="flex-1 flex items-center gap-3 px-4 py-2">
            <div className="flex items-center gap-2">
              <Mic className="w-5 h-5 text-blue-500" />
              <span className={`text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                Voice note ({formatDuration(recordingDuration)})
              </span>
            </div>
            <audio 
              src={audioUrl || undefined} 
              controls 
              className="flex-1 h-8"
              style={{ maxWidth: '200px' }}
            />
            <button
              onClick={cancelRecording}
              className={`p-2 rounded-full transition-colors ${
                isDark ? 'text-gray-400 hover:text-red-400 hover:bg-gray-700' : 'text-gray-500 hover:text-red-500 hover:bg-gray-200'
              }`}
              title="Delete voice note"
            >
              <Trash2 className="w-5 h-5" />
            </button>
            <button
              onClick={sendVoiceNote}
              disabled={sending}
              className={`p-3 rounded-full transition-all ${
                sending ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'
              } text-white`}
              title="Send voice note"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        ) : (
          // Normal text input - WhatsApp style layout
          <>
            {/* Input container with emoji + attach stacked on left, text area expanding wider */}
            <div className={`flex-1 flex items-end gap-1.5 rounded-2xl border px-1 ${
              isDark 
                ? 'bg-gray-800 border-gray-600' 
                : 'bg-white border-gray-300'
            }`}>
              {/* Text input — now takes full width inside the container */}
              <div className="flex-1 relative">
                <textarea
                  ref={textareaRef}
                  value={value}
                  onChange={handleChange}
                  onFocus={onComposerFocus}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  placeholder={placeholder}
                  disabled={disabled}
                  rows={1}
                  spellCheck={true}
                  autoComplete="on"
                  autoCorrect="on"
                  autoCapitalize="sentences"
                  enterKeyHint="send"
                  inputMode="text"
                  className={`w-full py-2.5 pl-3 pr-2 bg-transparent border-none resize-none focus:outline-none focus:ring-0 ${
                    isDark 
                      ? 'text-gray-100 placeholder-gray-500' 
                      : 'text-gray-900 placeholder-gray-400'
                  } text-base`}
                  style={{ minHeight: '80px', maxHeight: '240px', fontSize: '16px' }}
                />
                
                {/* Mention dropdown */}
                {showMentionDropdown && filteredMembers.length > 0 && (
                  <div 
                    ref={mentionDropdownRef}
                    className={`absolute bottom-full left-0 mb-1 w-full max-h-48 overflow-y-auto rounded-lg shadow-2xl border z-[140] ${
                      isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
                    }`}
                  >
                    {filteredMembers.map(member => (
                      <button
                        key={member.user_id}
                        onClick={() => insertMention(member)}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                          isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm ${
                          isDark ? 'bg-gray-600' : 'bg-gray-400'
                        }`}>
                          {member.avatar_url ? (
                            <img src={member.avatar_url} alt="" className="w-full h-full rounded-full object-cover" />
                          ) : (
                            member.display_name.charAt(0).toUpperCase()
                          )}
                        </div>
                        <div>
                          <p className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                            {member.display_name}
                          </p>
                          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {member.email}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Send or Voice/Dictation button - WhatsApp style single button on right */}
            {value.trim() || pendingAttachments.length > 0 ? (
              <button
                onClick={handleSend}
                disabled={disabled || sending}
                className={`p-2.5 rounded-full transition-all ${
                  disabled || sending
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-green-500 hover:bg-green-600'
                } text-white`}
                title="Send message"
              >
                {sending ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Send className="w-5 h-5" />
                )}
              </button>
            ) : onVoiceNote ? (
              // Voice recording button: press and hold to record, release to send
              <button
                onPointerDown={(e) => {
                  e.preventDefault();
                  activeRecordingGestureRef.current = true;
                  recordingPointerIdRef.current = e.pointerId;
                  recordingStartPointRef.current = { x: e.clientX, y: e.clientY };
                  e.currentTarget.setPointerCapture?.(e.pointerId);
                  void startRecording();
                }}
                onContextMenu={(e) => e.preventDefault()}
                className={`p-2.5 rounded-full transition-colors touch-none select-none ${
                  isRecording
                    ? 'bg-red-500 text-white animate-pulse'
                    : isDark 
                        ? 'bg-green-600 text-white hover:bg-green-500' 
                        : 'bg-green-500 text-white hover:bg-green-600'
                }`}
                title={isRecording 
                  ? 'Release to send voice note'
                  : 'Press and hold to record — slide up to lock, slide left to cancel'}
              >
                <Mic className="w-5 h-5" />
              </button>
            ) : enableSpeechToText && dictationSupported ? (
              // Only speech-to-text available (no voice notes)
              <button
                onClick={toggleDictation}
                className={`p-2.5 rounded-full transition-colors ${
                  isDictating 
                    ? 'bg-red-500 text-white animate-pulse' 
                    : isDark 
                      ? 'bg-green-600 text-white hover:bg-green-500' 
                      : 'bg-green-500 text-white hover:bg-green-600'
                }`}
                title={isDictating ? 'Stop dictation' : 'Tap to speak'}
              >
                {isDictating ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </button>
            ) : (
              <button
                onClick={handleSend}
                disabled={true}
                className="p-2.5 rounded-full bg-gray-400 cursor-not-allowed text-white"
                title="Send message"
              >
                <Send className="w-5 h-5" />
              </button>
            )}
          </>
        )}
      </div>
      </div>

      <SimpleCameraModal camera={camera} title="Take Photo" />

      {emojiPickerPortal}
    </>
  );
}
